<script>
import app from 'app';
import _ from 'lodash-extended';
import AssetUsed from '../lightbox/AssetUsed.vue';
import AssetRemove from './AssetRemove.vue';
import AssetLocate from './AssetLocate.vue';
import AssetAdd from './AssetAdd.vue';
import AssetEdit from './AssetEdit.vue';
import AssetCut from './AssetCut.vue';

export default {
  // language=Vue
  props: {
    asset: { type: Object, required: true },
    selected: { type: Boolean, default: false },
    showActions: { type: Boolean, default: true },
    // Show thumbnail only
    overlay: { type: Boolean, default: false },
    canAdd: { type: Boolean, default: true },
    customOverlayMode: { type: Boolean, default: false },
    mediaCoAssets: { type: Array, default: () => [] },
  },

  components: {
    'wt-asset-remove': AssetRemove,
    'wt-asset-add': AssetAdd,
    'wt-asset-edit': AssetEdit,
    'wt-asset-cut': AssetCut,
    'wt-asset-used': AssetUsed,
    'wt-asset-locate': AssetLocate,
  },

  data() {
    return {
      imageError: false,
      loading: true,
    };
  },

  computed: {
    isUsed() {
      return (
        this.asset._isInTimeline ||
        this.mediaCoAssets.includes(this.asset.metadata.assetUUID)
      );
    },
    isCommercial() {
      return app.assetType.isCommercial(this.asset);
    },
    isPinned() {
      return this.asset.pinned;
    },
    isCopyrighted() {
      return app.assetType.isCopyRighted(this.asset);
    },
    isSOCIAL() {
      return app.assetType.isSOCIAL(this.asset);
    },
    isINFOGRX() {
      return app.assetType.isINFOGRX(this.asset);
    },
    isInMyGallery() {
      return app.assetType.isInMyGallery(this.asset);
    },
    metaRestrictions() {
      return (this.asset.metadata.restrictions || '').normalizeSpace();
    },
    isAssetRestricted() {
      return (
        (!_.isEmpty(this.metaRestrictions) &&
          !app.assetType.isDynamicProvider(this.asset)) ||
        this.hasExpirationDate
      );
    },
    assetRestrictions() {
      let rv = this.metaRestrictions;
      if (this.asset.metadata.expirationDate) {
        rv +=
          (rv ? '<br/>' : '') +
          app.culture
            .get('generic.EXPIRATION_DATE_RESTRICTIONS')
            .format(
              app
                .moment(this.asset.metadata.expirationDate)
                .format('MM/DD/YYYY')
            );
      }
      if (app.assetType.isCommercial(this.asset)) {
        rv +=
          (!_.isEmpty(rv) ? ' - ' : '') +
          app.culture.get('generic.FOR_COMMERCIAL_USE');
      }
      if (app.assetType.isEditorial(this.asset) && !_.isEmpty(rv)) {
        rv +=
          (!_.isEmpty(rv) ? ' - ' : '') +
          app.culture.get('generic.FOR_EDITORIAL_USE');
      }
      return rv;
    },
    isGIF() {
      return app.assetType.isGIF(this.asset);
    },
    isMAP() {
      return app.assetType.isMAP(this.asset);
    },
    isImage() {
      return (
        app.assetType.isOnlyIMAGE(this.asset) ||
        app.assetType.isSOCIALIMAGE(this.asset)
      );
    },
    isVideo() {
      return (
        app.assetType.isFRAGMENT(this.asset) ||
        app.assetType.isSOCIALFRAGMENT(this.asset)
      );
    },
    isWEBPAGE() {
      return app.assetType.isWEBPAGE(this.asset);
    },
    isWALLPAPER() {
      return app.assetType.isWALLPAPER(this.asset);
    },
    isINSTAGRAM() {
      return app.assetType.isINSTAGRAM(this.asset);
    },
    isTWITTER() {
      return app.assetType.isTWITTER(this.asset);
    },
    isFRAGMENT() {
      return app.assetType.isFRAGMENT(this.asset);
    },
    fairUse() {
      return (
        !this.asset.metadata.licensed &&
        !app.assetType.isTWITTER(this.asset) &&
        !app.assetType.isWEBPAGE(this.asset) &&
        !app.assetType.isMAP(this.asset)
      );
    },
    hasExpirationDate() {
      const expirationDate = this.asset.metadata.expirationDate;
      if (
        _.isUndefined(expirationDate) ||
        Object.getOwnPropertyDescriptor(
          tk.context._expirationExcludeProviders,
          this.asset.metadata.providerId
        )
      ) {
        return false;
      }
      // If expiration date is more than 10 years from now, it should not be displayed to the user at all.
      const deltaYears = app.moment(expirationDate).diff(app.moment(), 'years');
      return deltaYears < 10;
    },
    publicationDate() {
      const millisecondsFromNow =
        this.$store.state.currentTime -
        (this.asset.metadata.publicationDate ||
          this.asset.metadata.creationDate);
      return app.TIME.getTimeAgo(millisecondsFromNow);
    },
    publicationDateFull() {
      return app
        .moment(
          this.asset.metadata.publicationDate ||
            this.asset.metadata.creationDate
        )
        .format('MM/DD/YYYY hh:mma');
    },
    iconsForOverlayAsset() {
      const arr = [];
      if (this.isGIF) {
        arr.push({
          iconClass: 'wticon wticon-gif_icon',
          boxClass: 'gif',
          title: '3rd party content. \n Consider fair use factors.',
        });
      }
      if (this.isMAP) {
        arr.push({
          iconClass: 'wticon wticon-instagram-location',
          boxClass: 'map',
          title: 'Map',
        });
      }
      if (this.isImage) {
        arr.push({
          iconClass: 'wticon wticon-picture-sun',
          boxClass: 'image',
          title: 'Image',
        });
      }
      if (this.isWALLPAPER) {
        arr.push({
          iconClass: 'wticon wticon-Bucket',
          boxClass: 'wallpaper',
          title: 'Color Card',
        });
      }
      if (this.isWEBPAGE) {
        arr.push({
          iconClass: 'wticon wticon-globe',
          boxClass: 'webpage',
          title: 'Web Page',
        });
      }
      if (this.isVideo) {
        arr.push({
          iconClass: 'wticon wticon-camera',
          boxClass: 'fragment',
          title: 'Fragment',
        });
      }
      return arr;
    },
    icons() {
      const arr = [];
      if (this.isFRAGMENT && !this.customOverlayMode) {
        const duration = this.asset.endTime - this.asset.startTime;
        arr.push({
          boxClass: 'fragment',
          iconText: app.moment.utc(duration * 1000).format('m:ss'),
        });
      }
      if (!this.customOverlayMode) {
        if (this.isAssetRestricted) {
          arr.push({
            iconClass: 'wticon wticon-Restriction_v8',
            showRestrictions: true,
            boxClass: 'restricted',
          });
        }
      }
      if (this.isGIF) {
        arr.push({
          iconClass: 'wticon wticon-gif_icon',
          boxClass: 'gif',
          title: '3rd party content. \n Consider fair use factors.',
        });
      }
      if (this.isMAP) {
        arr.push({
          iconClass: 'wticon wticon-instagram-location',
          boxClass: 'map',
          title: 'Map',
        });
      }
      if (this.isWALLPAPER) {
        arr.push({
          iconClass: 'wticon wticon-Bucket',
          boxClass: 'wallpaper',
          title: 'Color Card',
        });
      }
      if (this.isWEBPAGE) {
        arr.push({
          iconClass: 'wticon wticon-globe',
          boxClass: 'webpage',
          title: 'Web Page',
        });
      }
      if (this.isINSTAGRAM) {
        arr.push({
          iconClass: 'wticon wticon-instagram',
          boxClass: 'instagram',
          title: '3rd party content. \n Consider fair use factors.',
        });
      }
      if (this.isTWITTER) {
        arr.push({
          iconClass: 'wticon wticon-twitter',
          boxClass: 'twitter',
          title: '3rd party content. \n Consider fair use factors.',
        });
      }
      return arr;
    },

    isPreparing() {
      const previewOp = this.asset._previewOp;
      return _.isDefined(previewOp) && !/done/.test(previewOp);
    },

    isPreparingFailed() {
      const previewOp = this.asset._previewOp;
      return _.isDefined(previewOp) && /fail/i.test(previewOp);
    },

    displayDuration() {
      return this.getDisplayDuration(this.asset);
    },
    isSocialImageOrVideo() {
      return (
        app.assetType.isSOCIAL(this.asset) ||
        app.assetType.isSOCIALFRAGMENT(this.asset)
      );
    },
    containsRestrictions: function () {
      let result = false;
      if (
        _.isDefined(this.asset.metadata.restrictions) &&
        !_.isEmpty(this.asset.metadata.restrictions.trim())
      ) {
        result = true;
      }
      return result;
    },
    containsExpirationDate: function () {
      const expirationDate = this.asset.metadata.expirationDate;
      if (
        _.isUndefined(expirationDate) ||
        Object.getOwnPropertyDescriptor(
          tk.context._expirationExcludeProviders,
          this.asset.metadata.providerId
        )
      ) {
        return false;
      }
      // If expiration date is more than 10 years from now, it should not be displayed to the user at all.
      const deltaYears = app.moment(expirationDate).diff(app.moment(), 'years');
      return deltaYears < 10;
    },
    getRestrictions: function () {
      function formatExpirationDateMessage(expirationDate) {
        const expirationDateFormatted = app
          .moment(expirationDate)
          .format('MM/DD/YYYY');
        return app.culture.generic.EXPIRATION_DATE_RESTRICTIONS.format(
          expirationDateFormatted
        );
      }

      const expirationDate = this.asset.metadata.expirationDate;
      const isEditorial = app.assetType.isEditorial(this.asset);
      const isCommercial = app.assetType.isCommercial(this.asset);
      let returnString = this.asset.metadata.restrictions || '';

      if (app.assetType.isFairUse(this.asset) && !this.containsRestrictions) {
        returnString = app.culture.generic.FAIR_USE_MESSAGE;
      } else if (this.containsRestrictions) {
        if (this.containsExpirationDate) {
          returnString += formatExpirationDateMessage(expirationDate);
        }
      } else if (this.containsExpirationDate) {
        returnString = formatExpirationDateMessage(expirationDate);
      }

      if (isEditorial) {
        returnString +=
          (!_.isEmpty(returnString) ? ' - ' : '') +
          app.culture.generic.FOR_EDITORIAL_USE;
      } else if (isCommercial) {
        returnString +=
          (!_.isEmpty(returnString) ? ' - ' : '') +
          app.culture.generic.FOR_COMMERCIAL_USE;
      }
      return returnString;
    },
    isEditorialOrCommercialRestrictionsOnly() {
      return (
        !this.asset.isContainsRestrictions &&
        !app.assetType.isFairUse(this.asset) &&
        (app.assetType.isEditorial(this.asset) ||
          app.assetType.isCommercial(this.asset))
      );
    },
  },

  methods: {
    previewAsset(event) {
      event.preventDefault();
      const closestActionNode = event.target.closest('.-action');
      if (closestActionNode && this.$el.contains(closestActionNode)) {
        return;
      }
      if (event.metaKey) {
        this.$log(this.asset);
      } else {
        this.$emit('preview-asset', this.asset);
      }
    },

    previewVideoAsset(event) {
      event && event.preventDefault();
      if (event && event.metaKey) {
        // if
      } else {
        this.$emit('preview-asset', this.asset);
      }
    },

    onImageError(/* e*/) {
      this.imageError = true;
    },
  },
};
</script>
